import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { IUser, UserRole } from '@epicuro-next/data/users';
import {
  getValueFromConfigState,
  selectConfigurationsState,
  selectConfigurationValueFrom,
  selectToggleDiscountLabelByWsp,
} from '@epicuro-next/platform/configuration';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { State, userFeatureKey } from './user-state.reducer';

export const featureSelector = createFeatureSelector<State>(userFeatureKey);

export const selectPreventZoomRule = createSelector(
  selectConfigurationsState,
  (config) =>
    getValueFromConfigState<
      PulseConfigurationKeys.PreventZoomByPixelRatio,
      PulseConfigurations
    >(
      config,
      PULSE_FEATURE_NAME,
      PulseConfigurationKeys.PreventZoomByPixelRatio,
      pulseConfigurationDefaultValues[
        PulseConfigurationKeys.PreventZoomByPixelRatio
      ],
    ),
);

export const selectUser = createSelector(
  featureSelector,
  (state) => state.current,
);

export const selectUserLoading = createSelector(
  featureSelector,
  (state) => state.loading,
);

export const selectUserLoggedId = createSelector(
  featureSelector,
  (state) => state.loggedId,
);

export const selectUserError = createSelector(
  featureSelector,
  (state) => state.error,
);

export const selectUserName = createSelector(selectUser, (current: IUser) =>
  current
    ? {
        displayName: current.displayName,
        firstName: current.firstName,
        lastName: current.lastName,
      }
    : null,
);

export const selectUserAvatar = createSelector(
  selectUser,
  (current: IUser) => current?.avatarSource,
);

export const selectUserEmail = createSelector(
  selectUser,
  (current: IUser) => current?.email,
);

export const selectUserRoles = createSelector(selectUser, (current: IUser) => {
  const userRoles = [...(current?.roles ?? [])];
  if (userRoles.length === 0) {
    userRoles.push(UserRole.User);
  }
  return userRoles;
});

export const selectIsAdmin = createSelector(
  selectUserRoles,
  (roles: UserRole[]) => roles?.some((role) => role === UserRole.Administrator),
);

export const selectIsUserWithNoRoles = createSelector(
  selectUserRoles,
  (roles?: UserRole[]) => roles?.length === 0,
);

export const selectIsCustomerService = createSelector(
  selectUserRoles,
  (roles: UserRole[]) =>
    roles?.some((role) => role === UserRole.CustomerService),
);

export const selectIsAssortmentsUser = createSelector(
  selectUserRoles,
  (roles: UserRole[]) =>
    roles?.some((role) => role === UserRole.AssortmentsUser),
);

export const selectIsSalesRep = createSelector(
  selectUserRoles,
  (roles: UserRole[]) =>
    roles?.some((role) => role === UserRole.SalesRepresentative),
);

export const selectIsSalesRepOrAdmin = createSelector(
  selectIsAdmin,
  selectIsSalesRep,
  (isAdmin, isSalesRep) => isAdmin || isSalesRep,
);

export const selectIsAdminOrCustomerService = createSelector(
  selectIsAdmin,
  selectIsCustomerService,
  (isAdmin, isCustomerService) => isAdmin || isCustomerService,
);

export const selectIsSameUser = (id: string) =>
  createSelector(
    selectUser,
    (current: IUser) => current.externalId?.toLowerCase() === id?.toLowerCase(),
  );

export const selectIsSameUserByExternalId = createSelector(
  selectUser,
  (current: IUser, externalId: string) =>
    current.externalId?.toLowerCase() === externalId?.toLowerCase(),
);

export const selectChangingPassword = createSelector(
  featureSelector,
  (state) => state.changingPassword,
);

export const selectChangePasswordErrors = createSelector(
  featureSelector,
  (state) => state.changePasswordErrors,
);

export const selectShowWholeSalePrices = createSelector(
  featureSelector,
  (state) => Boolean(state.showWholeSalePrices),
);

export const selectHideDiscountLabelByWsp = createSelector(
  featureSelector,
  selectToggleDiscountLabelByWsp,
  (state, toggleDiscountLabelByWsp) =>
    !Boolean(state.showWholeSalePrices) && toggleDiscountLabelByWsp,
);

export const selectShowDarkMode = createSelector(
  featureSelector,
  (state) => state.darkMode,
);

export const selectPreventZoom = createSelector(
  featureSelector,
  (state) => state.preventZoom,
);

export const selectAdvancedMDOEnabled = createSelector(
  featureSelector,
  (state) => state.advancedMDOEnabled,
);

export const selectAdvancedMDOConfigAvailable = selectConfigurationValueFrom<
  PulseConfigurationKeys.AdvancedMDOAvailable,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.AdvancedMDOAvailable,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.AdvancedMDOAvailable],
);

export const selectAdvancedMDOSizesConfigAvailable =
  selectConfigurationValueFrom<
    PulseConfigurationKeys.AdvancedMDOSizesAvailable,
    PulseConfigurations
  >(
    PULSE_FEATURE_NAME,
    PulseConfigurationKeys.AdvancedMDOSizesAvailable,
    pulseConfigurationDefaultValues[
      PulseConfigurationKeys.AdvancedMDOSizesAvailable
    ],
  );

export const selectAdvancedMDOAvailableAndEnabled = createSelector(
  selectAdvancedMDOEnabled,
  selectAdvancedMDOConfigAvailable,
  (enabled, available) => Boolean(enabled && available),
);

export const selectUnsignedUserTerms = createSelector(
  featureSelector,
  (state) => state.unsignedUserTerms,
);

export const selectAcceptingUserTerms = createSelector(
  featureSelector,
  (state) => state.acceptingUserTerm,
);

export const selectUserAvatarLoading = createSelector(
  featureSelector,
  (state) => state.uploadingAvatar,
);

export const selectPreferences = createSelector(
  featureSelector,
  (state) => state.current?.preferences,
);

export const selectPreferredScaleSet = createSelector(
  featureSelector,
  (state) => state.current?.preferences?.preferredScaleSet,
);

export const selectPreferredShippingOptionId = createSelector(
  featureSelector,
  (state) => state.current?.preferences?.preferredShippingOption,
);

export const selectAssortmentList = createSelector(
  featureSelector,
  (state) => state.current?.preferences?.relatedProductsFilterList ?? [],
);

export const selectIsUpdatingPreferences = createSelector(
  featureSelector,
  (state) => state.updatingPreferences,
);

export const selectIsSubscribedToAccountBaskets = createSelector(
  selectUser,
  (current) => current?.isSubscribedToAccountBaskets,
);

export const selectUserId = createSelector(
  selectUser,
  (current: IUser) => current?.userId,
);

export const selectUserExternalId = createSelector(
  selectUser,
  (current: IUser) => current?.externalId,
);

export const selectPrivacyStatements = createSelector(
  featureSelector,
  (state) => state.privacyStatements,
);

export const selectTradeTerms = createSelector(
  featureSelector,
  (state) => state.tradeTerms,
);

export const selectUserAgreements = createSelector(
  featureSelector,
  (state) => state.userAgreements,
);

export const selectPreferredCultureInfo = createSelector(
  selectUser,
  (current: IUser) => current?.preferredCultureInfo,
);
